// React
import React from "react";
// redux hooks
import { useFieldArray, Controller } from "react-hook-form";
// BASE components
import BASESvgIcon from '../../../../../../@BASE/core/BASESvgIcon';
// material-ui components
import Accordion from '@mui/material/Accordion';
import AccordionActions from '@mui/material/AccordionActions';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import Box from "@mui/system/Box";
import Button from "@mui/material/Button"
import Divider from '@mui/material/Divider';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import FormControlLabel from '@mui/material/FormControlLabel';
import IconButton from "@mui/material/IconButton";
import Checkbox from '@mui/material/Checkbox';
import TextField from "@mui/material/TextField";
import Timeline from '@mui/lab/Timeline';
import TimelineConnector from '@mui/lab/TimelineConnector';
import TimelineContent from '@mui/lab/TimelineContent';
import TimelineDot from '@mui/lab/TimelineDot';
import TimelineItem, { timelineItemClasses } from '@mui/lab/TimelineItem';
import TimelineSeparator from '@mui/lab/TimelineSeparator';
import Typography from "@mui/material/Typography";
// custom components
import NumberedHeading from "../../../../../shared-components/forms/NumberedHeading";
import WYSIWYGEditor from "../../../../../shared-components/WYSIWYGEditor";

export default function ContentMaterialsArray({ control, register, setValue, getValues, errors, inputDir, nestIndex }) {
  // setup field array
  const { fields, append, remove, move } = useFieldArray({
    control,
    name: 'content.materials',
  });

  return (
    <>
      <Timeline
        sx={{
          [`& .${timelineItemClasses.root}:before`]: {
            flex: 0,
            padding: 0,
          },
        }}
        className="px-0"
      >
        {fields.map((item, index) => {
          return (
            <TimelineItem key={item.id}>
              <TimelineSeparator>
                <TimelineDot color="secondary" sx={{marginTop: '22px'}} />
                <TimelineConnector />
              </TimelineSeparator>
              <TimelineContent className="py-0">
                <Accordion
                  defaultExpanded
                  className="bg-transparent shadow-none"
                >
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    aria-controls={`${index}`}
                    id={`${index}`}
                    className="py-0 px-16 m-0 rounded bg-white shadow-sm"
                    sx={{
                      border: 'solid 1px',
                      borderColor: '#bdbdbd'
                    }}
                  >
                    <Typography className="grow m-0 font-semibold">
                      Material {index + 1}
                    </Typography>
                    {index !== 0 &&
                      <IconButton 
                        className="ml-16 mr-8"
                        aria-label="move up"
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          move(index, index - 1);
                        }}
                      >
                        <BASESvgIcon size={20}>heroicons-solid:arrow-circle-up</BASESvgIcon>
                      </IconButton>
                    }
                    {index !== fields.length - 1 &&
                      <IconButton 
                        className="mx-8"
                        aria-label="move down"
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          move(index, index + 1);
                        }}
                      >
                        <BASESvgIcon size={20}>heroicons-solid:arrow-circle-down</BASESvgIcon>
                      </IconButton>
                    }
                    <IconButton
                      className="ml-8 mr-16"
                      aria-label="delete"
                      color="error"
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        remove(index);
                      }}
                    >
                      <BASESvgIcon size={20}>heroicons-outline:trash</BASESvgIcon>
                    </IconButton>
                  </AccordionSummary>
                  <AccordionDetails className="p-0">
                    <Box
                      className="p-0"
                    >
                      <NumberedHeading number="1" title="Material Title (EN)" instructions="Enter a title for the authentic material in English" />
                      <Controller
                        control={control}
                        name={`content.materials.${index}.material_title_en`}
                        render={({ field }) => (
                          <TextField
                            className="mt-16"
                            {...field}
                            label="Title (EN)"
                            placeholder="Title (EN)"
                            id="video"
                            error={!!errors.content?.materials?.[index]?.material_title_en}
                            helperText={errors.content?.materials?.[index]?.material_title_en?.message}
                            variant="outlined"  
                            fullWidth
                            required
                          />
                        )}
                      />

                      <Divider className="my-16" />

                      <NumberedHeading number="2" title="Material Title (TL)" instructions="Please give title to the authentic material in the TL" />
                      <Controller
                        control={control}
                        name={`content.materials.${index}.material_title_tl`}
                        render={({ field }) => (
                          <TextField
                            className="mt-16"
                            {...field}
                            label="Title (TL)"
                            placeholder="Title (TL)"
                            id="video"
                            error={!!errors.content?.materials?.[index]?.material_title_tl}
                            helperText={errors.content?.materials?.[index]?.material_title_tl?.message}
                            variant="outlined"  
                            fullWidth
                            required
                            dir={inputDir}
                          />
                        )}
                      />

                      <Divider className="my-16" />

                      <NumberedHeading number="3" title="Link" instructions="Please give link to audio/video to use if available" />
                      <Controller
                        control={control}
                        name={`content.materials.${index}.link`}
                        render={({ field }) => (
                          <TextField
                            className="mt-16"
                            {...field}
                            label="Link"
                            placeholder="Link"
                            id="video"
                            error={!!errors.content?.materials?.[index]?.link}
                            helperText={errors.content?.materials?.[index]?.link?.message}
                            variant="outlined"  
                            fullWidth
                            required
                          />
                        )}
                      />

                      <Divider className="my-16" />

                      <NumberedHeading number="4" title="Sharepoint URL" instructions="Please provide the link to the material on Sharepoint" />
                      <Controller
                        control={control}
                        name={`content.materials.${index}.sp_url`}
                        render={({ field }) => (
                          <TextField
                            className="mt-16"
                            {...field}
                            label="Sharepoint URL"
                            placeholder="Sharepoint URL"
                            id="video"
                            error={!!errors.content?.materials?.[index]?.sp_url}
                            helperText={errors.content?.materials?.[index]?.sp_url?.message}
                            variant="outlined"  
                            fullWidth
                          />
                        )}
                      />

                      <Divider className="my-16" />

                      <NumberedHeading number="5" title="Media Material" instructions="Check the box if this material is for media" />
                      <Controller
                        control={control}
                        name={`content.materials.${index}.media_material`}
                        render={({ field }) => (
                          <FormControlLabel
                            control={
                              <Checkbox 
                                onChange={(ev) => {
                                  field.onChange(ev.target.checked);
                                }}
                                checked={field.value}
                              />
                            }
                            label='Media Material'
                          />
                        )}
                      />

                      <Divider className="my-16" />

                      <NumberedHeading number="6" title="Media URL" instructions="Please provide the link to the hosted media material. Leave this for the devs." />
                      <Controller
                        control={control}
                        name={`content.materials.${index}.media_url`}
                        render={({ field }) => (
                          <TextField
                            className="mt-16"
                            {...field}
                            label="Media URL"
                            placeholder="Media URL"
                            id="video"
                            error={!!errors.content?.materials?.[index]?.media_url}
                            helperText={errors.content?.materials?.[index]?.media_url?.message}
                            variant="outlined"  
                            fullWidth
                          />
                        )}
                      />

                      <Divider className="my-16" />

                      <NumberedHeading number="7" title="Material Reference ID" instructions="Please provide the page id for material. Leave this for the devs." />
                      <Controller
                        control={control}
                        name={`content.materials.${index}.media_page_id`}
                        render={({ field }) => (
                          <TextField
                            className="mt-16"
                            {...field}
                            label="Media Page ID"
                            placeholder="Media Page ID"
                            id="media_page_id"
                            error={!!errors.content?.materials?.[index]?.media_page_id}
                            helperText={errors.content?.materials?.[index]?.media_page_id?.message}
                            variant="outlined"  
                            fullWidth
                          />
                        )}
                      />
                      <Divider className="mt-16" />
                      
                    </Box>
                  </AccordionDetails>
                  <AccordionActions>
                    {index !== 0 &&
                      <Button
                        variant="outlined"
                        onClick={() => move(index, index - 1)}
                        startIcon={<BASESvgIcon size={20}>heroicons-solid:arrow-circle-up</BASESvgIcon>}
                        size="small"
                      >
                        Move Up
                      </Button>
                    }
                    {index !== fields.length - 1 &&
                      <Button
                        variant="outlined"
                        onClick={() => move(index, index + 1)}
                        startIcon={<BASESvgIcon size={20}>heroicons-solid:arrow-circle-down</BASESvgIcon>}
                        size="small"
                      >
                        Move Down
                      </Button>
                    }
                    <Button
                      variant="contained"
                      onClick={() => remove(index)}
                      color="error"
                      startIcon={<BASESvgIcon size={20}>heroicons-outline:trash</BASESvgIcon>}
                      size="small"
                    >
                      Remove
                    </Button>
                  </AccordionActions>
                </Accordion>
              </TimelineContent>
            </TimelineItem>
          );
        })}
      </Timeline>
      <Box className="flex items-center mt-0 p-0">
        <Button
          className="group flex items-center mt-8 rounded cursor-pointer"
          color="secondary"
          variant="contained"
          onClick={() => append({ 
            material_title_en: "",
            material_title_tl: "",  
            link: "",
            sp_url: "",
            media_material: false,
            media_url: "",
          })}
        >
          <BASESvgIcon size={20}>heroicons-solid:plus-circle</BASESvgIcon>
          <span className="ml-8 font-medium text-secondary group-hover:underline">
            Add Material
          </span>
        </Button>
      </Box>
    </>
  );
}
